import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { UserPlus, Printer, LogOut, Trash2, Loader2, ShieldCheck, Clock, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_75f76eb1-ed0f-4a2e-8af9-a573c953ed2b/artifacts/jcb4nruu_7d3b96f2-f08e-453b-8c2c-591199068bb4.jpeg";
const qrImg = (token) => `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(token)}&margin=0`;

const emptyForm = {
  name: "", company: "", purpose: "", host_user_id: "",
  phone: "", email: "", validity_hours: 24,
};

function fmt(dtIso) {
  if (!dtIso) return "-";
  try { return new Date(dtIso).toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return dtIso; }
}

function StatusBadge({ v }) {
  const isCheckedOut = !!v.checked_out_at;
  const isExpired = v.status === "expired" || (v.valid_until && new Date(v.valid_until) < new Date() && !isCheckedOut);
  if (isCheckedOut) return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-slate-200 text-slate-700"><CheckCircle2 className="h-3 w-3" /> Saída</span>;
  if (isExpired) return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-700"><Clock className="h-3 w-3" /> Expirado</span>;
  return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-emerald-100 text-emerald-700"><ShieldCheck className="h-3 w-3" /> Ativo</span>;
}

function VisitorBadge({ visitor }) {
  return (
    <div className="visitor-badge" style={{
      width: 500, height: 320, position: "relative", background: "#fff",
      fontFamily: "Arial, sans-serif", border: "2px solid #f97316", borderRadius: 10, overflow: "hidden",
    }}>
      <div style={{ background: "#f97316", color: "#fff", padding: "10px 18px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <img src={LOGO_URL} alt="" style={{ height: 44, background: "#fff", borderRadius: 4, padding: 2 }} crossOrigin="anonymous" />
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 20, fontWeight: 900, letterSpacing: 1 }}>VISITANTE</div>
          <div style={{ fontSize: 10, opacity: 0.9 }}>ACESSO TEMPORÁRIO</div>
        </div>
      </div>
      <div style={{ padding: "14px 18px", display: "grid", gridTemplateColumns: "1fr 140px", gap: 14 }}>
        <div>
          <div style={{ fontSize: 10, color: "#64748b", fontWeight: 700 }}>NOME</div>
          <div style={{ fontSize: 18, fontWeight: 900, color: "#0b1f3a", textTransform: "uppercase", lineHeight: 1.1, marginBottom: 8 }}>{visitor.name}</div>
          {visitor.company && (<>
            <div style={{ fontSize: 10, color: "#64748b", fontWeight: 700 }}>EMPRESA</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#0b1f3a", marginBottom: 8 }}>{visitor.company}</div>
          </>)}
          <div style={{ fontSize: 10, color: "#64748b", fontWeight: 700 }}>MOTIVO</div>
          <div style={{ fontSize: 12, color: "#0b1f3a", marginBottom: 8 }}>{visitor.purpose}</div>
          {visitor.host_name && (<>
            <div style={{ fontSize: 10, color: "#64748b", fontWeight: 700 }}>ANFITRIÃO</div>
            <div style={{ fontSize: 12, color: "#0b1f3a" }}>{visitor.host_name}</div>
          </>)}
        </div>
        <div style={{ textAlign: "center" }}>
          <img src={qrImg(visitor.qr_token)} alt="QR" crossOrigin="anonymous"
               style={{ width: 130, height: 130, border: "2px solid #0b1f3a", borderRadius: 4 }} />
          <div style={{ fontSize: 8, marginTop: 3, color: "#64748b", fontFamily: "monospace" }}>{visitor.qr_token}</div>
        </div>
      </div>
      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, background: "#0b1f3a", color: "#fff", padding: "6px 18px", display: "flex", justifyContent: "space-between", fontSize: 10 }}>
        <span>Válido até: <b style={{ color: "#f97316" }}>{fmt(visitor.valid_until)}</b></span>
        <span>EP3D • Santarém</span>
      </div>
    </div>
  );
}

export default function Visitors() {
  const [visitors, setVisitors] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("active");
  const [form, setForm] = useState(emptyForm);
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [printVisitor, setPrintVisitor] = useState(null);

  const load = async () => {
    try {
      const [v, u] = await Promise.all([
        axios.get(`${API}/visitors?status=${filter}`),
        axios.get(`${API}/users`),
      ]);
      setVisitors(v.data);
      setUsers(u.data.filter(x => x.role !== "display"));
    } catch (e) { toast.error("Erro ao carregar"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filter]);

  const stats = useMemo(() => {
    const active = visitors.filter(v => v.status === "active" && !v.checked_out_at).length;
    return { total: visitors.length, active };
  }, [visitors]);

  const createVisitor = async () => {
    if (!form.name || form.name.length < 2) { toast.error("Nome obrigatório"); return; }
    if (!form.purpose || form.purpose.length < 2) { toast.error("Motivo obrigatório"); return; }
    setCreating(true);
    try {
      const payload = { ...form };
      if (!payload.host_user_id) delete payload.host_user_id;
      const host = users.find(u => u.id === payload.host_user_id);
      if (host) payload.host_name = host.name || host.user_code;
      const r = await axios.post(`${API}/visitors`, payload);
      toast.success(`Crachá gerado para ${r.data.name}`);
      setForm(emptyForm);
      setShowDialog(false);
      setPrintVisitor(r.data);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
    finally { setCreating(false); }
  };

  const checkOut = async (id) => {
    setBusyId(id);
    try {
      await axios.post(`${API}/visitors/${id}/check-out`);
      toast.success("Saída registada");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
    finally { setBusyId(null); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remover este registo de visitante?")) return;
    setBusyId(id);
    try {
      await axios.delete(`${API}/visitors/${id}`);
      toast.success("Removido");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
    finally { setBusyId(null); }
  };

  const doPrint = (visitor) => {
    setPrintVisitor(visitor);
    setTimeout(() => window.print(), 300);
  };

  if (loading) return <div className="flex justify-center p-10"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="visitors-page">
      <style>{`
        .visitor-print-area { display: none; }
        @media print {
          @page { size: A5 landscape; margin: 12mm; }
          body * { visibility: hidden !important; }
          .visitor-print-area, .visitor-print-area * { visibility: visible !important; }
          .visitor-print-area { display: block !important; position: absolute; left: 0; top: 0; }
        }
      `}</style>

      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2"><ShieldCheck className="h-8 w-8 text-orange-500" /> Visitantes</h1>
          <p className="text-sm text-muted-foreground mt-1">Crachás temporários com validade e QR para controlo de entradas.</p>
        </div>
        <div className="flex gap-2 items-center">
          <div className="flex gap-2 items-center bg-white border rounded-lg px-3 py-1.5 text-sm">
            <ShieldCheck className="h-4 w-4 text-emerald-600" />
            <span className="font-semibold">{stats.active}</span> ativos / <span className="text-muted-foreground">{stats.total}</span>
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40" data-testid="visitor-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Ativos</SelectItem>
              <SelectItem value="expired">Expirados</SelectItem>
              <SelectItem value="checked_out">Com saída</SelectItem>
              <SelectItem value="all">Todos</SelectItem>
            </SelectContent>
          </Select>
          <Button className="bg-orange-500 hover:bg-orange-600" onClick={() => setShowDialog(true)} data-testid="new-visitor-btn">
            <UserPlus className="h-4 w-4 mr-2" /> Novo Visitante
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-700">
            <tr>
              <th className="text-left p-3">Nome / Empresa</th>
              <th className="text-left p-3">Motivo</th>
              <th className="text-left p-3">Anfitrião</th>
              <th className="text-left p-3">Entrada</th>
              <th className="text-left p-3">Válido até</th>
              <th className="text-left p-3">Estado</th>
              <th className="text-right p-3">Ações</th>
            </tr>
          </thead>
          <tbody>
            {visitors.length === 0 && (
              <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">Sem visitantes neste filtro</td></tr>
            )}
            {visitors.map(v => (
              <tr key={v.id} className="border-t hover:bg-slate-50" data-testid={`visitor-row-${v.id}`}>
                <td className="p-3">
                  <div className="font-semibold">{v.name}</div>
                  {v.company && <div className="text-xs text-muted-foreground">{v.company}</div>}
                  {v.phone && <div className="text-xs text-muted-foreground">{v.phone}</div>}
                </td>
                <td className="p-3 text-slate-700">{v.purpose}</td>
                <td className="p-3 text-slate-700">{v.host_name || "-"}</td>
                <td className="p-3 text-xs text-slate-600">{fmt(v.checked_in_at)}</td>
                <td className="p-3 text-xs text-slate-600">{fmt(v.valid_until)}</td>
                <td className="p-3"><StatusBadge v={v} /></td>
                <td className="p-3">
                  <div className="flex gap-2 justify-end">
                    <Button size="sm" variant="outline" onClick={() => doPrint(v)} data-testid={`print-${v.id}`}>
                      <Printer className="h-4 w-4" />
                    </Button>
                    {!v.checked_out_at && (
                      <Button size="sm" variant="outline" onClick={() => checkOut(v.id)} disabled={busyId === v.id} data-testid={`checkout-${v.id}`}>
                        {busyId === v.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogOut className="h-4 w-4" />}
                      </Button>
                    )}
                    <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => remove(v.id)} disabled={busyId === v.id} data-testid={`delete-${v.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Preview do último gerado */}
      {printVisitor && (
        <div className="bg-white p-4 rounded-lg border shadow-sm">
          <div className="flex justify-between items-center mb-3">
            <h2 className="text-lg font-semibold">Crachá de {printVisitor.name}</h2>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => window.print()} data-testid="print-preview-btn"><Printer className="h-4 w-4 mr-1" /> Imprimir</Button>
              <Button size="sm" variant="ghost" onClick={() => setPrintVisitor(null)}>Fechar</Button>
            </div>
          </div>
          <div className="flex justify-center">
            <VisitorBadge visitor={printVisitor} />
          </div>
        </div>
      )}

      {/* Print-only area */}
      {printVisitor && (
        <div className="visitor-print-area">
          <VisitorBadge visitor={printVisitor} />
        </div>
      )}

      {/* Dialog: novo visitante */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg" data-testid="new-visitor-dialog">
          <DialogHeader><DialogTitle>Registar Novo Visitante</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nome completo *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="visitor-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Empresa</Label>
                <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="visitor-company" />
              </div>
              <div>
                <Label>Telefone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="visitor-phone" />
              </div>
            </div>
            <div>
              <Label>Motivo da visita *</Label>
              <Input value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} placeholder="Ex: Manutenção da máquina 3" data-testid="visitor-purpose" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Anfitrião</Label>
                <Select value={form.host_user_id} onValueChange={(v) => setForm({ ...form, host_user_id: v })}>
                  <SelectTrigger data-testid="visitor-host"><SelectValue placeholder="(opcional)" /></SelectTrigger>
                  <SelectContent>
                    {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name} ({u.user_code})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Validade (horas)</Label>
                <Input type="number" min={1} max={72} value={form.validity_hours}
                       onChange={(e) => setForm({ ...form, validity_hours: parseInt(e.target.value) || 24 })}
                       data-testid="visitor-validity" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDialog(false)}>Cancelar</Button>
            <Button className="bg-orange-500 hover:bg-orange-600" onClick={createVisitor} disabled={creating} data-testid="create-visitor-btn">
              {creating ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <UserPlus className="h-4 w-4 mr-1" />}
              Gerar Crachá
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
